import React, { useEffect, useState, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// Components
import PersonalInfo from "./profile/PersonalInfo";
import PreviousEmployment from "./profile/PreviousEmployment";
import AccoundStationary from "./profile/AccoundStationary";
import FamilyDetails from "./profile/FamilyDetails";
import NominationDetails from "./profile/NominationDetails";
import Documents from "./profile/Documents";
import { toasterError, toasterSuccess } from "../../Components/toaster";

const features = [
  { id: 1, title: "Personal Information", path: "personal" },
  { id: 2, title: "Previous Employment", path: "previous-employment" },
  { id: 3, title: "Accounts & Statutory", path: "statutory" },
  { id: 4, title: "Family Details", path: "family" },
  { id: 5, title: "Nomination Details", path: "nomination" },
  { id: 6, title: "Documents", path: "documents" },
];

export const MyProfile = () => {
  const [getState, setState] = useState(1);
  const [canUserEdit, setCanUserEdit] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef(null);
  const savingRef = useRef(false);
  const childRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setIsDirty(false);
  }, [getState]);

  useEffect(() => {
    const hash = location.hash?.replace("#", "");
    if (!hash) {
      setState(1);
      return;
    }
    const found = features.find((f) => f.path === hash);
    if (found) setState(found.id);
  }, [location.hash]);

  useEffect(() => {
    if (childRef.current && childRef.current.isEditable !== undefined) {
      setCanUserEdit(childRef.current.isEditable);
    } else {
      setCanUserEdit(true);
    }
  }, [getState, childRef.current?.isEditable]);

  const handleGlobalSave = async () => {
    if (childRef.current && childRef.current.handleChildSave) {
      savingRef.current = true;
      setIsSaving(true);

      const result = await childRef.current.handleChildSave();

      setIsSaving(false);
      savingRef.current = false;

      if (result.success) {
        const nextState = Math.min(getState + 1, features?.length);
        if (nextState !== getState) {
          setState(nextState);
          const nextFeature = features?.find((feature) => feature?.id === nextState);
          if (nextFeature) {
            navigate(`#${nextFeature?.path}`);
          }
        }
        toasterSuccess("Data successfully saved! 🎉");
      } else {
        toasterError(result.error || "Failed to save data. Please try again.");
      }
    } else {
      toasterError("No save handler found for this tab.");
    }
  };

  const renderComponent = () => {
    switch (getState) {
      case 1:
        return (
          <PersonalInfo
            ref={childRef}
            onEditStatusChange={setCanUserEdit}
            onDirtyChange={setIsDirty}
          />
        );
      case 2:
        return (
          <PreviousEmployment
            ref={childRef}
            onEditStatusChange={setCanUserEdit}
            onDirtyChange={setIsDirty}
          />
        );
      case 3:
        return (
          <AccoundStationary
            ref={childRef}
            onEditStatusChange={setCanUserEdit}
            onDirtyChange={setIsDirty}
          />
        );
      case 4:
        return (
          <FamilyDetails
            ref={childRef}
            onEditStatusChange={setCanUserEdit}
            onDirtyChange={setIsDirty}
          />
        );
      case 5:
        return (
          <NominationDetails
            ref={childRef}
            onEditStatusChange={setCanUserEdit}
            onDirtyChange={setIsDirty}
          />
        );
      case 6:
        return (
          <Documents
            ref={childRef}
            onEditStatusChange={setCanUserEdit}
            onDirtyChange={setIsDirty}
          />
        );
      default:
        return (
          <PersonalInfo
            ref={childRef}
            onEditStatusChange={setCanUserEdit}
            onDirtyChange={setIsDirty}
          />
        );
    }
  };

  return (
    <div className="px-4">
      <div className="flex flex-col px-4  py-2 md:p-6 relative gap-4 max-w-full overflow-x-hidden">
        <div className="">
          <span className="font-semibold text-xl text-gray-800">
            My Profile
          </span>
          <p className="text-gray-500 text-sm">
            Manage your personal details securely.
          </p>
        </div>

        <div
          ref={navRef}
          className=" md:flex mt-4 overflow-x-auto scrollbar-hide"
        >
          {features.map((feature) => {
            const isActive = getState === feature.id;
            return (
              <NavLink
                key={feature.id}
                to={`#${feature.path}`}
                onClick={() => {
                  navigate("#" + feature.path);
                }}
                className={`text-center mr-6 pb-2 text-[16px] font-semibold ${isActive ? "border-b-[3px] border-[#00B8D9] text-gray-900" : "text-gray-500"}`}
              >
                {feature.title}
              </NavLink>
            );
          })}
        </div>

        <div>{renderComponent()}</div>
        {canUserEdit && (
          <div className="flex flex-col sm:flex-row sm:justify-end items-stretch sm:items-center gap-2 md:gap-4 mt-6 pb-6 w-full">
            {getState !== 6 ? (
              <>
                <button className="font-semibold text-xs md:text-base px-3 md:px-6 py-1.5 md:py-2 w-full sm:w-auto hover:bg-gray-100 rounded">
                  Cancel
                </button>
                <button
                  onClick={handleGlobalSave}
                  disabled={isSaving || !isDirty}
                  className={`${isSaving || !isDirty ? "bg-gray-400" : "bg-[#00B8D9]"} text-white font-semibold text-xs md:text-base px-3 md:px-6 py-1.5 md:py-2 rounded w-full sm:w-auto transition-colors`}
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </>
            ) : (
              <button
                onClick={handleGlobalSave}
                disabled={isSaving || !isDirty}
                className={`${isSaving || !isDirty ? "bg-gray-400" : "bg-[#00B8D9]"} text-white font-semibold text-xs md:text-base px-3 md:px-6 py-1.5 md:py-2 rounded w-full sm:w-auto transition-colors`}
              >
                {isSaving ? "Uploading..." : "Upload Documents"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyProfile;
